import { reactive, computed, toRef, type Ref } from 'vue'

export type AutoRtInfo = Record<string, any>

export function createEmptyAutoRTInfo(): AutoRtInfo {
  return {
    logoImage: '',
    clientId: '',
    dialogName: '',
    clientName: '',
    termurl: '',
    privacyurl: '',
    occupationurl: '',
    handlingurl: '',
    clientServiceName: '',
    privacyLabel: '',
    showTrailingBr: false,
    permissionType: 0,
    oldPermissionType: 0,
    googleConversationId: null,
    googleConversationLabel: null,
    sendApiEndpointKey: '',
    agreed: false
  }
}

/** Vue 2.7 / 3 共通のモジュール級 reactive（Vue.prototype は使わない） */
export const autoRtState = reactive({
  info: createEmptyAutoRTInfo(),
  /**
   * tryClaimSend で info を空にしたあとも、関連 RT1.0（old permission）の
   * playlist 抑止・送客後 prune が参照する。再取得成功で破棄。
   */
  lastClaimed: null as AutoRtInfo | null,
  /** キック後〜 getDisableRtCondition 再取得成功まで true（再送・再武装禁止） */
  sentUntilRefresh: false,
  /** consult in-flight（beforeunload 用） */
  isSending: false
})

function useAutoRtSession() {
  const info = computed(() => autoRtState.info)
  const isActive = computed(() => !!autoRtState.info.clientId)
  const isAgreedForSend = computed(
    () => !!autoRtState.info.clientId && autoRtState.info.agreed === true
  )
  const isSentUntilRefresh = computed(
    () => autoRtState.sentUntilRefresh === true
  )
  const isSending: Ref<boolean> = toRef(autoRtState, 'isSending')

  function getAutoRtInfo(): AutoRtInfo {
    return autoRtState.info
  }

  /**
   * バナー用の live session。無ければ直前 claim の snapshot。
   * playlist の old permission 抑止と updateRtCondition のペア prune 用。
   */
  function getRelatedAutoRtInfo(): AutoRtInfo {
    if (autoRtState.info && autoRtState.info.clientId) {
      return autoRtState.info
    }
    return autoRtState.lastClaimed || createEmptyAutoRTInfo()
  }

  function setAutoRtInfo(next: AutoRtInfo) {
    autoRtState.info = { ...createEmptyAutoRTInfo(), ...next }
  }

  function clearAutoRtInfo() {
    autoRtState.info = createEmptyAutoRTInfo()
  }

  function setAgreed(agreed: boolean) {
    autoRtState.info = { ...autoRtState.info, agreed: !!agreed }
  }

  /**
   * 送客キックを同期 claim する。成功時のみ snapshot を返し、再送不可にする。
   * 条件: clientId あり・agreed・!sentUntilRefresh
   */
  function tryClaimSend(): AutoRtInfo | null {
    const current = autoRtState.info
    if (
      autoRtState.sentUntilRefresh ||
      !current.clientId ||
      current.agreed !== true
    ) {
      return null
    }
    const snapshot = { ...current }
    autoRtState.sentUntilRefresh = true
    autoRtState.isSending = true
    autoRtState.lastClaimed = snapshot
    autoRtState.info = createEmptyAutoRTInfo()
    return snapshot
  }

  /** consult 完了（成否問わず）。sentUntilRefresh は維持する */
  function finishSending() {
    autoRtState.isSending = false
  }

  /** getDisableRtCondition 再取得成功時のみ。再武装を許可する */
  function allowAutoRtRearm() {
    autoRtState.sentUntilRefresh = false
    autoRtState.lastClaimed = null
  }

  return {
    state: autoRtState,
    info,
    isActive,
    isAgreedForSend,
    isSentUntilRefresh,
    isSending,
    getAutoRtInfo,
    getRelatedAutoRtInfo,
    setAutoRtInfo,
    clearAutoRtInfo,
    setAgreed,
    tryClaimSend,
    finishSending,
    allowAutoRtRearm
  }
}

/** Options API / 非 setup コンテキスト向け */
export function getAutoRtSession() {
  return useAutoRtSession()
}
